import {
  isCardContentKindReviewEligible,
  type CardContentKind,
  type CardReviewSettings,
} from "@-/dev.improve.haniverse.lib.cards.card-content";

/**
 * Which review kind the learner is practicing, picked from the Learn toolbar's Reviews menu, in the
 * menu's order. `all` is the normal deck (every enabled review kind plus new lessons); any other
 * focus serves only due reviews of that one kind and introduces no new lessons, so a learner can,
 * say, drill pronunciation on its own for a while.
 */
export const REVIEW_FOCUSES = [
  "all",
  "grapheme-meaning",
  "grapheme-pronunciation",
  "word-characters-in-context",
  "word-characters-in-sentence",
  "word-pronunciation-in-sentence",
] as const satisfies readonly ("all" | CardContentKind)[];

export type ReviewFocus = (typeof REVIEW_FOCUSES)[number];

/** Named after the drill header each kind shows ("Pronunciation Review", "Speaking Practice", …). */
export function reviewFocusLabel(reviewFocus: ReviewFocus): string {
  switch (reviewFocus) {
    case "all": {
      return "All";
    }
    case "grapheme-meaning": {
      return "Meaning";
    }
    case "grapheme-pronunciation": {
      return "Pronunciation";
    }
    case "word-characters-in-context": {
      return "Words";
    }
    case "word-characters-in-sentence": {
      return "Sentences";
    }
    case "word-pronunciation-in-sentence": {
      return "Speaking";
    }
  }
}

/** Whether the focus lets this card kind into the review queue. */
export function isCardContentKindInReviewFocus({
  cardContentKind,
  reviewFocus,
}: {
  cardContentKind: CardContentKind;
  reviewFocus: ReviewFocus;
}): boolean {
  return reviewFocus === "all" || reviewFocus === cardContentKind;
}

/**
 * Whether the focus can serve anything under the user's settings. A focus on a kind the settings
 * gate out (pronunciation while pinyin flashcards are off, …) would only ever show an empty deck,
 * so the menu leaves it out.
 */
export function isReviewFocusAvailable({
  reviewFocus,
  settings,
}: {
  reviewFocus: ReviewFocus;
  settings: CardReviewSettings;
}): boolean {
  return (
    reviewFocus === "all" ||
    isCardContentKindReviewEligible({ cardContentKind: reviewFocus, settings })
  );
}
